import React, { createContext, Dispatch, FC, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import decode from 'jwt-decode';
import { AuthService } from 'lib';
import { routeConstants } from 'routes';
import { logOutRequest } from 'store/App';

const WARNING_LEAD_SECONDS = parseInt(process.env.REACT_APP_WARNING_LEAD_SECONDS || '600', 10); // show warning when token will expire in N seconds
const DEFAULT_DEBOUNCE_CHECK_SESSION_MILLISECONDS = parseInt(process.env.REACT_APP_DEBOUNCE_CHECK_SESSION_MILLISECONDS || '120000', 10); // interval to show check the token

interface IProps {
  children: ReactNode;
}

interface ISession {
  isExpired: boolean;
  isWarningPeriod: boolean;
  secondsRemaining: number;
  date: Date;
}

const getSession = (token?: string): ISession | null => {
  if (!token) return null;

  const { exp } = decode<{ exp: number }>(token);
  if (!exp) return null;

  const tokenExpirationDate = new Date(0);
  tokenExpirationDate.setUTCSeconds(exp);
  const currentDate = Date.now();
  const secondsRemaining = Math.max((tokenExpirationDate.getTime() - currentDate) / 1000, 0);

  return {
    secondsRemaining,
    date: tokenExpirationDate,
    isExpired: secondsRemaining < 1,
    isWarningPeriod: secondsRemaining > 0 && secondsRemaining < WARNING_LEAD_SECONDS,
  };
};

interface ISessionContext {
  session: ISession | null;
  setSession: Dispatch<SetStateAction<ISession | null>>;
  getSession: (token: string) => ISession | null;
}

const SessionContext = createContext<ISessionContext | null>(null);

export const useSessionContext = () => useContext(SessionContext);

export const SessionProvider: FC<IProps> = ({ children }) => {
  const dispatch = useDispatch();
  const [session, setSession] = useState<ISession>(getSession(AuthService.getToken()));
  const [debounceCheckSessionMilliseconds, setDebounceCheckSessionMilliseconds] = useState<number>(DEFAULT_DEBOUNCE_CHECK_SESSION_MILLISECONDS);
  const memoizedGetSession = useCallback(getSession, []);

  useEffect(() => {
    const interval = setInterval(() => setSession(memoizedGetSession(AuthService.getToken())), debounceCheckSessionMilliseconds);
    return () => clearInterval(interval);
  }, [memoizedGetSession, debounceCheckSessionMilliseconds]);

  useEffect(() => {
    // Once we've entered the warning period, update the session interval check from 2 minutes to 1 minute
    if (session?.isWarningPeriod && debounceCheckSessionMilliseconds === DEFAULT_DEBOUNCE_CHECK_SESSION_MILLISECONDS) {
      setDebounceCheckSessionMilliseconds(60000);
    }

    // Check if the token is expired and log the user out when it is expired
    if (!session?.isWarningPeriod && session?.isExpired) {
      dispatch(logOutRequest(`${routeConstants.public.login}?message=session_expired`));
    }
  }, [dispatch, session]);

  return <SessionContext.Provider value={{ session, setSession, getSession: memoizedGetSession }}>{children}</SessionContext.Provider>;
};
